import { forwardRef, ReactNode } from "react";
import { DDSIconButton } from "../../Buttons/DDSIconButton/DDSIconButton";
import { IconX } from "@web-monorepo/dds-icons";
import styles from "./DDSScreenBanner.module.css";

export interface DDSScreenBannerProps {
  /**
   * The main content of the banner
   */
  children: ReactNode;

  /**
   * Optional close button handler. If provided, shows a close button.
   */
  onClose?: () => void;

  /**
   * Visual variant of the banner
   * @default "primary"
   */
  variant?: "primary" | "secondary" | "warning" | "info";

  /**
   * The name will get used for automated product events.
   * @see https://www.notion.so/classdojo/Automatic-Product-Events-for-Web-bfc580f10a914c3ba514e5ec20f8ef9e?pvs=4
   */
  "data-name": string;

  /**
   * Additional CSS class name applied to the outer banner element.
   */
  className?: string;

  /**
   * Additional CSS class name applied to the inner content wrapper.
   * Use this to override the default 800px max-width or otherwise restyle
   * the centered content area without coupling to the banner's internal DOM.
   */
  contentClassName?: string;
}

export const DDSScreenBanner = forwardRef<HTMLDivElement, DDSScreenBannerProps>(
  ({ children, onClose, variant = "primary", "data-name": dataName, className, contentClassName, ...props }, ref) => {
    const classNames = [styles.banner, styles[variant], className].filter(Boolean).join(" ");
    const contentClassNames = [styles.content, contentClassName].filter(Boolean).join(" ");

    return (
      <div ref={ref} className={classNames} data-name={dataName} {...props}>
        <div className={contentClassNames}>{children}</div>
        {onClose && (
          <DDSIconButton
            aria-label="Close"
            size="xsmall"
            data-name={`${dataName}.close`}
            onClick={onClose}
            tooltip={{
              content: "Close",
              dataName: "close-banner-tooltip",
            }}
            variant="white"
            className={styles.closeButton}
          >
            <IconX size="s" />
          </DDSIconButton>
        )}
      </div>
    );
  },
);

DDSScreenBanner.displayName = "DDSScreenBanner";
